import type {
  AiChatState,
  PickType,
  UiStreamAction,
  UiStreamHooks,
} from "@intelligems/ig-types";

import { AiChatStateProcessor } from "./ai-chat-state-processor";
import { ReasoningProcessor } from "./reasoning-processor";
import { TextProcessor } from "./text-processor";
import { ToolProcessor } from "./tool-processor";
import { getLastNodeByTurnId, getOrCreateTurnAndNode } from "./utils";

export class MessageStreamProcessor {
  static handleAddMessageToLastNode(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "addMessageToLastNode">
  ) {
    const node = getLastNodeByTurnId(draft, payload.turnId);

    if (node) {
      AiChatStateProcessor.pushMessageToNode(draft, node, payload);
    }
    // Only a user reply answers a parked input requirement — see
    // `clearSatisfiedInputRequirement`. This path carries assistant and tool
    // messages too, and clearing on those dropped the approval chips.
    AiChatStateProcessor.clearSatisfiedInputRequirement(draft, payload.message);
  }

  static handleAddStreamPart(
    draft: AiChatState,
    payload: PickType<UiStreamAction, "addStreamPart">,
    hooks?: UiStreamHooks
  ) {
    const { node } = getOrCreateTurnAndNode(draft, payload);

    if (TextProcessor.isTextPart(payload.streamPart)) {
      return TextProcessor.processStreamPart(
        node,
        payload.messageId,
        payload.streamPart,
        hooks
      );
    }

    if (ReasoningProcessor.isReasoningPart(payload.streamPart)) {
      return ReasoningProcessor.processStreamPart(
        node,
        payload.messageId,
        payload.streamPart
      );
    }

    if (ToolProcessor.isToolPart(payload.streamPart)) {
      return ToolProcessor.processStreamPart(
        draft,
        node,
        payload.messageId,
        payload.streamPart,
        hooks
      );
    }

    if (AiChatStateProcessor.isStatePart(payload.streamPart)) {
      return AiChatStateProcessor.handleStatePart(
        draft,
        node,
        payload.streamPart
      );
    }
  }

  static handleSetNextAction(
    draft: AiChatState,
    payload: Extract<UiStreamAction, { type: "setNextAction" }>
  ) {
    draft.nextAction = payload.nextAction;
    if (payload.nextAction) {
      draft.status = "waiting_for_input";
    }
  }
}
